if (!customElements.get('banner-takeover')) {
  const formatMoney = (cents) => {
    const amount = Number(cents) || 0;
    const moneyFormat = window.KROWN?.settings?.shop_money_format;

    if (typeof _formatMoney === 'function' && moneyFormat) {
      return _formatMoney(amount, moneyFormat);
    }

    return (amount / 100).toFixed(2);
  };

  const escapeHtml = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

  const cardImage = (card) => {
    if (!card) return '';

    const image = card.querySelector('img');
    if (!image) return '';

    return image.currentSrc || image.src || '';
  };

  const shipIntervals = new Map();

  const shipUnit = (interval, count) => {
    const units = {
      day: ['day', 'days'],
      week: ['week', 'weeks'],
      month: ['month', 'months'],
      year: ['year', 'years']
    };
    const pair = units[String(interval || '').toLowerCase()];
    if (!pair) return '';

    return Number(count) === 1 ? pair[0] : pair[1];
  };

  const formatShips = (template, interval, count) => {
    const unit = shipUnit(interval, count);
    const amount = Number(count);
    if (!unit || !Number.isFinite(amount) || amount <= 0) return '';

    return (template || 'Ships every @@count@@ @@interval@@')
      .replaceAll('@@count@@', String(amount))
      .replaceAll('@@interval@@', unit);
  };

  const deliveryInterval = (variantId) => {
    const key = String(variantId || '');
    if (!key) return Promise.resolve(null);
    if (shipIntervals.has(key)) return shipIntervals.get(key);

    const proxyUrl = window.jpapp?.proxyUrl || '';
    const apiPath = window.KROWN?.settings?.subscription_api?.get_selling_plan_info_by_variant_id || '';
    if (!proxyUrl || !apiPath) {
      const empty = Promise.resolve(null);
      shipIntervals.set(key, empty);
      return empty;
    }

    const request = fetch(`${proxyUrl}${apiPath}?variantId=${encodeURIComponent(key)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const sellingPlan = data?.sellingPlan;
        const deliveryPolicy = sellingPlan?.deliveryPolicy;
        if (!data || data.error || !deliveryPolicy?.interval) return null;

        const installment = sellingPlan.sellingPlanType === 'installment';
        const intervalCount = installment && sellingPlan.originalIntervalCount != null
          ? sellingPlan.originalIntervalCount
          : deliveryPolicy.intervalCount;

        return { interval: deliveryPolicy.interval, intervalCount };
      })
      .catch(() => null);

    shipIntervals.set(key, request);
    return request;
  };

  const lineProperties = (variantId, properties) => {
    if (typeof window.mergeCartLinePropertiesWithGlobalId !== 'function') return properties;

    return window.mergeCartLinePropertiesWithGlobalId(variantId, properties);
  };

  const manualPaymentSelected = () => {
    const manualTypes = window.KROWN?.settings?.manualPaymentTypes;
    if (!Array.isArray(manualTypes)) return false;

    const selected = window.cartDataForPaymentSelectionInCart?.attributes?._paymentMethod
      || window.cartAvailablePaymentMethods?.subscription?.[0]
      || 'Online';

    return manualTypes.includes(selected);
  };

  const formattedMonthlyBase = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return null;
    if (/^\d+\.?\d*$/.test(value)) {
      const amount = Number(value);
      if (!Number.isNaN(amount)) return amount.toFixed(2);
    }

    return value;
  };

  const monthlyBaseFor = (card, subscription, poland) => {
    const formatted = formattedMonthlyBase(card?.dataset.monthlyBase);
    if (poland) {
      if (!subscription || formatted === null) return null;
      if (String(card?.dataset.sku || '').split('_')[0] !== 'inst') return '';

      return formatted;
    }

    return formatted;
  };

  const productProperties = (card, { subscriptionType, planName, otsInstallment, subscription, poland }) => {
    const properties = {
      _discountvalue: card?.dataset.discountValue || '',
      _selling_plan_name: planName || '',
      _sellingplan: '',
      _ots_installment: otsInstallment ? 'true' : 'false',
      _subscriptionType: subscriptionType
    };
    const monthlyBase = monthlyBaseFor(card, subscription, poland);
    if (monthlyBase !== null) properties._monthly_base_total = monthlyBase;

    return properties;
  };

  const installmentFeeItem = async (manual) => {
    if (typeof window.buildInstallmentFeeItem !== 'function') return null;

    const fee = window.buildInstallmentFeeItem(manual);
    if (!fee) return null;

    try {
      const current = window.JPCartService?.peek?.() || (window.JPCartService ? await window.JPCartService.getCart() : null);
      if (current && typeof window.cartHasInstallmentFee === 'function' && window.cartHasInstallmentFee(current)) {
        return null;
      }
    } catch (error) {
      // Include the fee. Quantity is clamped to 1 after the add.
    }

    return fee;
  };

  const sectionDocument = async (sectionId) => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?section_id=${sectionId}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) return null;

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const refreshCartChrome = async (cart) => {
    const itemCount = Number(cart?.item_count);
    if (Number.isFinite(itemCount)) {
      const label = String(itemCount);
      document.querySelectorAll('cart-count-v2, [data-header-cart-count]').forEach((node) => {
        node.textContent = label;
        node.classList.toggle('is-empty', itemCount === 0);
      });
    }

    const helper = await sectionDocument('helper-cart').catch(() => null);
    if (helper) {
      const form = document.getElementById('AjaxCartForm');
      const formHtml = helper.getElementById('AjaxCartForm');
      if (form && formHtml) {
        form.innerHTML = formHtml.innerHTML;
        form.ajaxifyCartItems?.();
        form.initializeSubscriptionDetails?.();
      }

      const subtotal = document.getElementById('AjaxCartSubtotal');
      const subtotalHtml = helper.getElementById('AjaxCartSubtotal');
      if (subtotal && subtotalHtml) subtotal.innerHTML = subtotalHtml.innerHTML;

      const count = document.querySelector('#AjaxCartForm [data-cart-count]');
      if (count) {
        document.querySelectorAll('[data-header-cart-count]').forEach((node) => {
          node.textContent = count.textContent;
        });
      }

      document.getElementById('cart-recommendations-sidebar')?.generateRecommendations?.();
    }

    const drawerMarkup = await sectionDocument('cart-drawer-v2').catch(() => null);
    const drawer = document.querySelector('cart-drawer-v2');
    if (drawerMarkup && drawer) {
      const body = drawer.querySelector('[data-cart-v2-body]');
      const footer = drawer.querySelector('[data-cart-v2-footer]');
      const bodyHtml = drawerMarkup.getElementById('cart-drawer-v2-body');
      const footerHtml = drawerMarkup.getElementById('cart-drawer-v2-footer');
      const countHtml = drawerMarkup.getElementById('cart-drawer-v2-count');
      if (body && bodyHtml) body.innerHTML = bodyHtml.innerHTML;
      if (footer && footerHtml) footer.innerHTML = footerHtml.innerHTML;
      if (countHtml) {
        document.querySelectorAll('[data-cart-v2-count]').forEach((node) => {
          node.innerHTML = countHtml.innerHTML;
        });
      }
      drawer.closest('#site-cart-sidebar')?.classList.toggle('cart-is-empty', Boolean(body?.querySelector('.cart-v2-empty')));
    }

    if (typeof window.refreshCartPaymentMethods === 'function') {
      await window.refreshCartPaymentMethods().catch(() => {});
    }
  };

  const openCartDrawer = () => {
    const drawer = document.getElementById('site-cart-sidebar');
    if (drawer && typeof drawer.show === 'function') drawer.show();
  };

  const payloadText = (value) => {
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.map((entry) => payloadText(entry)).filter(Boolean).join(' ');
    if (value && typeof value === 'object') {
      return Object.values(value).map((entry) => payloadText(entry)).filter(Boolean).join(' ');
    }
    return '';
  };

  const cartFailureMessage = (error) => {
    const body = error && error.body;
    if (!body || typeof body !== 'object') return '';
    return payloadText(body.description) || payloadText(body.errors) || payloadText(body.message);
  };

  const isUnprocessableCart = (error) => {
    if (!error || typeof error !== 'object') return false;
    if (Number(error.status) === 422) return true;
    if (error.body && Number(error.body.status) === 422) return true;
    return typeof error.message === 'string' && error.message.includes('422');
  };

  const showDrawerCartError = (message) => {
    if (message === '') return;

    const drawer = document.getElementById('site-cart-sidebar');
    const current = drawer?.querySelector('[data-cart-v2-error]') || document.querySelector('[data-cart-v2-error]');
    if (current) {
      current.textContent = message;
      current.hidden = false;
      return;
    }

    const form = document.querySelector('#AjaxCartForm form');
    if (!form) return;

    form.querySelector('#product-page-form-error-cart-alert')?.remove();
    const alert = document.createElement('span');
    alert.className = 'alert alert--error';
    alert.id = 'product-page-form-error-cart-alert';
    alert.textContent = message;
    form.prepend(alert);
  };

  const revealCartFailure = (dialog, message) => {
    const reveal = () => {
      document.documentElement.classList.remove('banner-takeover-open');
      document.body.style.top = '';
      showDrawerCartError(message);
      const drawer = document.getElementById('site-cart-sidebar');
      if (drawer) drawer.dataset.cartV2ForceOpen = 'true';
      openCartDrawer();
    };

    if (dialog.open) {
      dialog.addEventListener('close', () => {
        requestAnimationFrame(reveal);
      }, { once: true });
      dialog.close();
      return;
    }

    reveal();
  };

  class VerticalCarousel {
    constructor(column, onChange, signal) {
      this.column = column;
      this.viewport = column.querySelector('[data-viewport]');
      this.track = column.querySelector('[data-track]');
      this.counter = column.querySelector('[data-counter]');
      this.prev = column.querySelector('[data-step="-1"]');
      this.next = column.querySelector('[data-step="1"]');
      this.onChange = onChange;
      this.signal = signal;
      this.index = 0;
      this.slot = 0;
      this.silent = false;
      this.scrollFrame = 0;
      this.scrollEndTimer = 0;

      const activeIndex = this.cards.findIndex((card) => card.classList.contains('is-active'));
      this.index = activeIndex > -1 ? activeIndex : 0;

      this.prev?.addEventListener('click', () => this.step(-1), { signal });
      this.next?.addEventListener('click', () => this.step(1), { signal });
      this.viewport?.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
      this.viewport?.addEventListener('scrollend', () => this.#onScrollEnd(), { signal });
      this.viewport?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowUp' || event.key === 'PageUp') {
          event.preventDefault();
          this.step(-1);
          return;
        }
        if (event.key === 'ArrowDown' || event.key === 'PageDown') {
          event.preventDefault();
          this.step(1);
        }
      }, { signal });
      signal?.addEventListener('abort', () => this.#stopScroll(), { once: true });
    }

    get cards() {
      if (!this.track) return [];

      return [...this.track.querySelectorAll('[data-variant-id]:not([data-clone])')].filter((card) => !card.hidden);
    }

    get count() {
      return this.cards.length;
    }

    get current() {
      return this.cards[this.index] || null;
    }

    setSlot(slot) {
      this.slot = slot;
    }

    layout() {
      this.track?.querySelectorAll('.banner-takeover__slot[data-clone]').forEach((clone) => clone.remove());
      if (this.track) this.track.style.transform = 'none';
      if (this.index > this.count - 1) this.index = 0;

      this.cards.forEach((card, index) => {
        card.dataset.loopIndex = String(index);
      });
      this.#rebuildClones();

      this.silent = true;
      this.#applyPad();
      this.#scrollToIndex(this.index, false);
      this.#paint(this.index);
      this.#updateChrome();
      this.silent = false;
    }

    step(direction) {
      if (this.count < 2) return;

      const nextIndex = (this.index + direction + this.count) % this.count;
      const leavingEnd = (direction > 0 && this.index === this.count - 1) || (direction < 0 && this.index === 0);
      const clone = leavingEnd ? this.#edgeClone(direction, nextIndex) : null;

      if (clone) {
        this.#scrollToSlot(clone, true);
        return;
      }

      this.#scrollToIndex(nextIndex, true);
    }

    #rebuildClones() {
      if (!this.track || this.count < 2) return;

      const slots = this.cards.map((card) => this.#box(card));

      for (let index = slots.length - 1; index >= 0; index -= 1) {
        this.track.prepend(this.#cloneSlot(slots[index], index));
      }

      slots.forEach((slot, index) => {
        this.track.append(this.#cloneSlot(slot, index));
      });
    }

    #cloneSlot(slot, index) {
      const copy = slot.cloneNode(true);
      copy.dataset.clone = 'true';
      copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      copy.querySelectorAll('[for]').forEach((element) => element.removeAttribute('for'));

      const card = copy.querySelector('[data-variant-id]');
      if (card) {
        card.dataset.clone = 'true';
        card.dataset.loopIndex = String(index);
        card.setAttribute('aria-hidden', 'true');
        card.classList.remove('is-active', 'is-neighbor');
      }

      return copy;
    }

    #edgeClone(direction, loopIndex) {
      const clones = [...(this.track?.querySelectorAll('.banner-takeover__slot[data-clone]') || [])].filter((slot) => {
        return Number(slot.querySelector('[data-variant-id]')?.dataset.loopIndex) === loopIndex;
      });

      if (!clones.length) return null;

      return direction > 0 ? clones[clones.length - 1] : clones[0];
    }

    #loopSlots() {
      if (!this.track) return [];

      return [...this.track.querySelectorAll('.banner-takeover__slot')].filter((slot) => {
        const card = slot.querySelector('[data-variant-id]');
        return card && !card.hidden && slot.offsetHeight > 0;
      });
    }

    #box(card) {
      return card.closest('.banner-takeover__slot') || card;
    }

    #contentTop(card) {
      const box = this.#box(card);
      const trackTop = this.track?.offsetTop || 0;

      return trackTop + box.offsetTop;
    }

    #applyPad() {
      if (!this.viewport) return;

      const cards = this.cards;
      const first = cards[0];
      const last = cards[cards.length - 1];
      if (!first || cards.length < 2) {
        this.viewport.style.paddingTop = '';
        this.viewport.style.paddingBottom = '';
        return;
      }

      const view = this.viewport.clientHeight;
      const top = Math.max(0, (view - this.#box(first).offsetHeight) / 2);
      const bottom = Math.max(0, (view - this.#box(last).offsetHeight) / 2);
      this.viewport.style.paddingTop = `${top}px`;
      this.viewport.style.paddingBottom = `${bottom}px`;
    }

    #scrollToIndex(index, animate) {
      const card = this.cards[index];
      if (!card) return;

      this.#scrollToSlot(this.#box(card), animate);
    }

    #scrollToSlot(slot, animate) {
      const card = slot?.querySelector('[data-variant-id]');
      if (!card || !this.viewport) return;

      const top = this.#contentTop(card) - ((this.viewport.clientHeight - slot.offsetHeight) / 2);
      const snap = this.viewport.style.scrollSnapType;
      if (!animate) this.viewport.style.scrollSnapType = 'none';
      this.viewport.scrollTo({ top: Math.max(0, top), behavior: animate ? 'smooth' : 'auto' });
      if (!animate) {
        this.viewport.offsetHeight;
        this.viewport.style.scrollSnapType = snap;
      }
    }

    #closestLoop() {
      const slots = this.#loopSlots();
      if (!this.viewport || !slots.length) return { index: 0, clone: false };

      const middle = this.viewport.scrollTop + (this.viewport.clientHeight / 2);
      let closest = 0;
      let clone = false;
      let best = Number.POSITIVE_INFINITY;

      slots.forEach((slot) => {
        const card = slot.querySelector('[data-variant-id]');
        const index = Number(card?.dataset.loopIndex);
        if (Number.isNaN(index)) return;

        const center = this.#contentTop(card) + (slot.offsetHeight / 2);
        const distance = Math.abs(center - middle);
        if (distance >= best) return;

        best = distance;
        closest = index;
        clone = slot.dataset.clone === 'true';
      });

      return { index: closest, clone };
    }

    #loopGap(index, activeIndex) {
      const count = this.count;
      if (count < 2) return index === activeIndex ? 0 : 2;

      const raw = Math.abs(index - activeIndex) % count;
      return Math.min(raw, count - raw);
    }

    #paint(activeIndex) {
      if (!this.track) return;

      this.track.querySelectorAll('[data-variant-id]').forEach((card) => {
        if (card.hidden) return;

        const index = Number(card.dataset.loopIndex);
        const gap = this.#loopGap(index, activeIndex);
        card.classList.toggle('is-active', gap === 0);
        card.classList.toggle('is-neighbor', gap === 1);
      });
    }

    #jumpToReal(index) {
      const card = this.cards[index];
      if (!card || !this.viewport) return;

      this.silent = true;
      this.viewport.classList.add('is-loop-jump');
      this.index = index;
      this.#paint(index);
      this.#scrollToSlot(this.#box(card), false);
      this.#updateChrome();

      requestAnimationFrame(() => {
        this.viewport?.classList.remove('is-loop-jump');
        this.silent = false;
      });
    }

    #stopScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.scrollEndTimer) window.clearTimeout(this.scrollEndTimer);
      this.scrollFrame = 0;
      this.scrollEndTimer = 0;
    }

    #onScroll() {
      if (this.silent || this.count < 2) return;

      if (this.scrollEndTimer) window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => this.#onScrollEnd(), 140);

      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (this.signal?.aborted || this.silent) return;

        const next = this.#closestLoop();
        this.#paint(next.index);
        if (next.index === this.index) return;

        this.index = next.index;
        this.#updateChrome();
        this.onChange?.();
      });
    }

    #onScrollEnd() {
      if (this.silent || this.count < 2) return;

      const next = this.#closestLoop();
      this.#paint(next.index);
      if (next.index !== this.index) {
        this.index = next.index;
        this.#updateChrome();
        this.onChange?.();
      }

      if (next.clone) this.#jumpToReal(next.index);
    }

    #updateChrome() {
      if (this.counter) {
        this.counter.textContent = this.count ? `${this.index + 1} / ${this.count}` : '';
      }

      const canScroll = this.count > 1;
      if (this.prev) this.prev.disabled = !canScroll;
      if (this.next) this.next.disabled = !canScroll;

      const arrows = this.column?.querySelector('.banner-takeover__arrows');
      if (arrows) arrows.hidden = !canScroll;
      this.column?.classList.toggle('is-single', !canScroll);
    }
  }

  class BannerTakeover extends HTMLElement {
    #abort = null;
    #shipRequest = 0;
    #modalAbort = null;
    #lock = false;
    #pageLocked = false;
    #scrollY = 0;
    #scrollbarHeld = false;

    connectedCallback() {
      if (this.#abort) this.#abort.abort();
      this.#abort = new AbortController();
      this.#lock = false;
      this.payMode = 'full';
      this.bannerIndex = 0;
      this.bannerPaused = false;
      this.bannerTimer = null;
      this.bannerSlides = [...this.querySelectorAll('.banner-takeover__slide')];
      this.#bind();
    }

    disconnectedCallback() {
      if (this.#abort) this.#abort.abort();
      if (this.#modalAbort) this.#modalAbort.abort();
      this.#stopBannerAutoplay();
      this.#releasePage();
      document.querySelectorAll(`dialog[data-banner-owner="${this.dataset.sectionId}"]`).forEach((dialog) => {
        if (dialog.parentElement === document.body) dialog.remove();
      });
    }

    #bind() {
      const signal = this.#abort.signal;

      this.addEventListener('click', (event) => this.#onClick(event), { signal });
      this.#renderDots();
      this.#moveBanner(0);

      const viewport = this.querySelector('[data-banner-viewport]');
      viewport?.addEventListener('pointerdown', (event) => this.#bannerPointer(event), { signal });
      viewport?.addEventListener('mouseenter', () => {
        this.bannerPaused = true;
      }, { signal });
      viewport?.addEventListener('mouseleave', () => {
        this.bannerPaused = false;
      }, { signal });
      window.addEventListener('resize', () => this.#onResize(), { signal });
      this.#startBannerAutoplay();
      document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), { signal });

      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('close', () => this.#onDialogClose(dialog), { signal });
        dialog.addEventListener('click', (event) => {
          const close = event.target.closest('[data-close]');
          if (event.target === dialog || close) dialog.close();
        }, { signal });
      });
    }

    #onClick(event) {
      const opener = event.target.closest('[data-takeover-open]');
      if (opener) {
        this.#openTakeover(opener.dataset.takeoverOpen, opener);
        return;
      }

      const terms = event.target.closest('[data-terms-open]');
      if (terms) {
        this.#openTerms(terms.dataset.termsOpen);
        return;
      }

      const close = event.target.closest('[data-close]');
      if (close) {
        close.closest('dialog')?.close();
        return;
      }

      const prev = event.target.closest('[data-banner-prev]');
      if (prev) {
        this.#moveBanner(this.bannerIndex - 1);
        return;
      }

      const next = event.target.closest('[data-banner-next]');
      if (next) {
        this.#moveBanner(this.bannerIndex + 1);
        return;
      }

      const dot = event.target.closest('[data-banner-dot]');
      if (dot) this.#moveBanner(Number(dot.dataset.bannerDot));
    }

    #renderDots() {
      const root = this.querySelector('[data-banner-dots]');
      if (!root) return;

      root.replaceChildren();
      this.bannerSlides.forEach((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'banner-takeover__dot';
        button.dataset.bannerDot = String(index);
        button.setAttribute('aria-label', `${index + 1} / ${this.bannerSlides.length}`);
        root.append(button);
      });
    }

    #startBannerAutoplay() {
      this.#stopBannerAutoplay();
      if (this.dataset.layout !== 'carousel') return;
      if (this.bannerSlides.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.bannerTimer = window.setInterval(() => {
        if (this.bannerPaused || this.activeModal?.open) return;
        this.#moveBanner(this.bannerIndex + 1, true);
      }, 6000);
    }

    #stopBannerAutoplay() {
      if (!this.bannerTimer) return;

      window.clearInterval(this.bannerTimer);
      this.bannerTimer = null;
    }

    #moveBanner(index, automatic = false) {
      if (!this.bannerSlides.length) return;

      const count = this.bannerSlides.length;
      this.bannerIndex = (index + count) % count;
      const track = this.querySelector('[data-banner-track]');
      if (track) track.style.transform = `translate3d(${this.bannerIndex * -100}%, 0, 0)`;

      this.querySelectorAll('[data-banner-dot]').forEach((dot, dotIndex) => {
        const selected = dotIndex === this.bannerIndex;
        dot.classList.toggle('is-selected', selected);
        if (selected) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (!automatic) this.#startBannerAutoplay();
    }

    #bannerPointer(event) {
      if (event.target.closest('button, a, dialog')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const startX = event.clientX;
      let delta = 0;

      const move = (moveEvent) => {
        delta = moveEvent.clientX - startX;
      };

      const up = () => {
        window.removeEventListener('pointermove', move);
        if (delta > 40) this.#moveBanner(this.bannerIndex - 1);
        if (delta < -40) this.#moveBanner(this.bannerIndex + 1);
      };

      window.addEventListener('pointermove', move, { signal: this.#abort.signal });
      window.addEventListener('pointerup', up, { signal: this.#abort.signal, once: true });
    }

    #onBlockSelect(event) {
      if (event.target.closest('banner-takeover') !== this) return;

      const slide = event.target.closest('.banner-takeover__slide');
      if (!slide) return;

      const index = this.bannerSlides.indexOf(slide);
      if (index >= 0) this.#moveBanner(index);
    }

    #dialog(name, id) {
      const escaped = window.CSS && window.CSS.escape ? window.CSS.escape(id) : id;
      const selector = `[data-${name}="${escaped}"]`;
      return this.querySelector(selector)
        || document.querySelector(`${selector}[data-banner-owner="${this.dataset.sectionId}"]`);
    }

    #scrollbarGap() {
      return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    }

    #holdScrollbar() {
      if (this.#scrollbarHeld) return;

      this.#scrollbarHeld = true;
      const gap = this.#scrollbarGap();
      if (gap) document.documentElement.style.setProperty('--banner-takeover-scrollbar', `${gap}px`);
    }

    #releaseScrollbar() {
      if (this.activeModal?.open) return;

      const owner = this.dataset.sectionId;
      const termsOpen = owner
        ? document.querySelector(`dialog.banner-takeover__terms-dialog[open][data-banner-owner="${owner}"]`)
        : null;
      if (termsOpen) return;

      this.#scrollbarHeld = false;
      document.documentElement.style.removeProperty('--banner-takeover-scrollbar');
      document.body.classList.remove('banner-takeover-terms-open');
      document.body.style.overflow = '';
    }

    #markTermsScroll(dialog) {
      const body = dialog.querySelector('.banner-takeover__terms-body');
      if (!body) return;

      body.classList.toggle('is-scrollable', body.scrollHeight > body.clientHeight + 1);
    }

    #openTerms(id) {
      const dialog = this.#dialog('terms-dialog', id);
      if (!dialog || dialog.open) return;

      const takeoverOpen = Boolean(this.activeModal?.open);
      const home = dialog.parentElement;
      document.body.append(dialog);
      this.#holdScrollbar();
      if (!takeoverOpen) {
        document.body.classList.add('banner-takeover-terms-open');
        document.body.style.overflow = 'hidden';
      }
      dialog.showModal();
      this.#markTermsScroll(dialog);
      dialog.addEventListener('close', () => {
        if (home && home.isConnected) home.append(dialog);
        this.#releaseScrollbar();
      }, { once: true });
      dialog.querySelector('[data-close]')?.focus({ preventScroll: true });
    }

    #openTakeover(id, opener) {
      const dialog = this.#dialog('takeover', id);
      if (!dialog) return;

      if (this.activeModal && this.activeModal !== dialog) this.activeModal.close();

      this.activeModal = dialog;
      this.opener = opener;
      this.purchaseType = dialog.dataset.purchaseType || 'ots';
      this.payMode = dialog.dataset.defaultPay === 'installment' ? 'installment' : 'full';
      this.modalHome = dialog.parentElement;
      document.body.append(dialog);
      this.#ensureLogo(dialog);
      this.#lockPage();
      dialog.showModal();
      this.#mountModal(dialog);
      dialog.querySelector('.banner-takeover__close--modal')?.focus();
    }

    #lockPage() {
      if (this.#pageLocked) return;

      this.#holdScrollbar();
      this.#pageLocked = true;
      this.#scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      document.body.style.top = `-${this.#scrollY}px`;
      document.documentElement.classList.add('banner-takeover-open');
    }

    #unlockPage() {
      if (this.activeModal?.open) return;

      const owner = this.dataset.sectionId;
      const termsOpen = owner
        ? document.querySelector(`dialog.banner-takeover__terms-dialog[open][data-banner-owner="${owner}"]`)
        : null;
      if (termsOpen) return;

      this.#releasePage();
    }

    #releasePage() {
      const locked = this.#pageLocked || document.documentElement.classList.contains('banner-takeover-open');
      this.#pageLocked = false;
      document.documentElement.classList.remove('banner-takeover-open');
      document.body.style.top = '';
      this.#releaseScrollbar();
      if (locked) window.scrollTo(0, this.#scrollY || 0);
    }

    #ensureLogo(modal) {
      const image = modal.querySelector('.banner-takeover__logo-image');
      if (!image || image.getAttribute('src')) return;

      const source = document.querySelector('#logo .logo-img:not(.logo-img--mobile) img')
        || document.querySelector('#logo .logo-img img');
      const src = source?.currentSrc || source?.getAttribute('src');
      if (!src) {
        image.closest('.banner-takeover__logo')?.setAttribute('hidden', '');
        return;
      }

      image.src = src;
      if (source.alt) image.alt = source.alt;
    }

    #mountModal(dialog) {
      if (this.#modalAbort) this.#modalAbort.abort();
      this.#modalAbort = new AbortController();
      const signal = this.#modalAbort.signal;
      const columns = dialog.querySelectorAll('[data-column]');

      this.primary = new VerticalCarousel(columns[0], () => this.#onPrimaryChange(), signal);
      this.secondary = new VerticalCarousel(columns[1], () => this.#renderSummary(), signal);

      dialog.querySelectorAll('[data-pay]').forEach((button) => {
        button.addEventListener('click', () => this.#setPayMode(button.dataset.pay), { signal });
      });

      dialog.addEventListener('change', (event) => {
        if (!event.target.closest('[data-pay-select]')) return;
        this.#onVariantChange(event.target);
      }, { signal });

      dialog.querySelector('[data-add]')?.addEventListener('click', () => {
        this.#addToCart(dialog);
      }, { signal });

      dialog.addEventListener('click', (event) => {
        const terms = event.target.closest('[data-terms-open]');
        if (!terms) return;

        this.#openTerms(terms.dataset.termsOpen);
      }, { signal });

      if (this.purchaseType === 'subscription') {
        this.#applyPayMode();
        this.#applyGiftVisibility();
      }

      this.#ensureLogo(dialog);
      this.#syncSlots();
      this.#renderSummary();

      const settle = () => {
        if (signal.aborted || this.activeModal !== dialog) return;

        this.#syncSlots();
        this.#renderSummary();
      };

      requestAnimationFrame(() => requestAnimationFrame(settle));
      document.fonts?.ready?.then(settle);
    }

    #resetAddButton(dialog) {
      const button = dialog.querySelector('[data-add]');
      if (!button) return;

      delete button.dataset.loading;
      button.removeAttribute('aria-busy');
      button.disabled = false;
      const label = button.querySelector('[data-add-label]');
      if (label && dialog.dataset.addLabel) label.textContent = dialog.dataset.addLabel;
    }

    #onDialogClose(dialog) {
      if (!dialog.dataset.takeover || dialog !== this.activeModal) return;

      this.#resetAddButton(dialog);
      if (this.#modalAbort) this.#modalAbort.abort();
      this.activeModal = null;
      this.primary = null;
      this.secondary = null;
      this.#unlockPage();

      if (this.modalHome) this.modalHome.append(dialog);

      this.opener?.focus();
    }

    #onPrimaryChange() {
      if (this.#lock) return;
      if (this.purchaseType === 'subscription') {
        this.#applyGiftVisibility();
        this.secondary?.layout();
      }

      this.#renderSummary();
    }

    #applyGiftVisibility() {
      const variantId = this.primary?.current?.dataset.variantId || '';
      const track = this.secondary?.track;
      if (!track) return;

      const giftCards = [...track.querySelectorAll('[data-variant-id]')];
      giftCards.forEach((card) => {
        const match = card.dataset.giftFor === variantId;
        card.hidden = !match;
        card.classList.toggle('is-active', match);
        card.classList.toggle('is-neighbor', false);
      });

      const visible = giftCards.filter((card) => !card.hidden);
      track.querySelectorAll('[data-gift-empty]').forEach((empty) => {
        empty.hidden = visible.length > 0 || empty.dataset.giftFor !== variantId;
      });

      if (this.secondary) this.secondary.index = 0;
    }

    #syncSlots() {
      const modal = this.activeModal;
      if (!modal || !this.primary || !this.secondary) return;

      this.#lock = true;
      const cards = [...modal.querySelectorAll('.banner-takeover__card:not(.banner-takeover__card--gift):not([data-clone])')].filter((card) => !card.hidden);
      cards.forEach((card) => {
        card.style.minHeight = '';
        card.style.maxHeight = '';
        card.style.overflow = '';
      });

      const tallest = cards.reduce((max, card) => Math.max(max, card.offsetHeight), 0);
      const slot = tallest;

      if (slot) {
        modal.style.setProperty('--banner-takeover-slot', `${slot}px`);
        cards.forEach((card) => {
          card.style.minHeight = `${slot}px`;
        });
        this.primary.setSlot(slot);
        this.secondary.setSlot(slot);
      }

      this.primary.layout();
      this.secondary.layout();
      this.#lock = false;
    }

    #onResize() {
      if (!this.activeModal?.open) return;

      this.#syncSlots();
      this.#renderSummary();
    }

    #setPayMode(mode) {
      this.payMode = mode === 'installment' ? 'installment' : 'full';
      const productId = this.primary?.current?.dataset.productId;
      this.#paintPayOptions();
      this.#applyPayMode();

      if (this.primary && productId) {
        const index = this.primary.cards.findIndex((card) => card.dataset.productId === productId);
        this.primary.index = index > -1 ? index : 0;
      }

      if (this.purchaseType === 'subscription') this.#applyGiftVisibility();
      this.#syncSlots();
      this.#renderSummary();
    }

    #paintPayOptions() {
      this.activeModal?.querySelectorAll('[data-pay]').forEach((button) => {
        const selected = button.dataset.pay === this.payMode;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    #originalCards() {
      const track = this.primary?.track;
      if (!track) return [];

      return [...track.querySelectorAll('[data-product-id]:not([data-clone])')];
    }

    #activePicker(card) {
      const named = card?.querySelector(`[data-pay-picker="${this.payMode}"]`);
      if (named && !named.hidden) return named;

      return [...(card?.querySelectorAll('[data-pay-picker]') || [])].find((picker) => !picker.hidden) || null;
    }

    #selectedOption(card) {
      const select = this.#activePicker(card)?.querySelector('[data-pay-select]');
      if (!select || !select.options.length) return null;

      return select.selectedOptions[0] || select.options[0] || null;
    }

    #applySelectedOption(card) {
      const option = this.#selectedOption(card);
      if (!option) return;

      card.dataset.variantId = option.value;
      card.dataset.price = option.dataset.price || '0';
      card.dataset.compare = option.dataset.compare || '0';
      card.dataset.planId = option.getAttribute('data-plan-id') || '';
      card.dataset.planName = option.getAttribute('data-plan-name') || '';
      card.dataset.discountValue = option.getAttribute('data-discount-value') || '';
      card.dataset.monthlyBase = option.getAttribute('data-monthly-base') || '';
      card.dataset.sku = option.getAttribute('data-sku') || '';
      card.dataset.ships = option.getAttribute('data-ships') || '';
      card.dataset.available = option.dataset.available || 'true';

      const price = card.querySelector('[data-price-text]');
      if (price) price.textContent = formatMoney(card.dataset.price);

      const compareAt = card.querySelector('[data-compare-at-text]');
      const regular = card.querySelector('[data-regular-text]');
      if (compareAt || regular) {
        const regularCents = Number(option.getAttribute('data-regular'));
        const compareAtCents = Number(option.getAttribute('data-compare-at'));
        const hasRegular = Number.isFinite(regularCents) && regularCents > 0;
        const showCompareAt = hasRegular && Number.isFinite(compareAtCents) && compareAtCents > regularCents;
        if (compareAt) {
          compareAt.hidden = !showCompareAt;
          if (showCompareAt) compareAt.textContent = formatMoney(compareAtCents);
        }
        if (regular && hasRegular) {
          regular.hidden = false;
          regular.textContent = formatMoney(regularCents);
          regular.classList.toggle('banner-takeover__price--sale', showCompareAt);
        }
      }

      const compare = card.querySelector('[data-compare-text]');
      const compareCents = Number(card.dataset.compare) || 0;
      const priceCents = Number(card.dataset.price) || 0;
      if (compare) {
        compare.hidden = compareCents <= priceCents;
        compare.textContent = compareCents > priceCents ? formatMoney(compareCents) : '';
      }

      const frequency = card.querySelector('[data-frequency]');
      const modal = this.activeModal;
      if (frequency && modal) {
        frequency.textContent = this.payMode === 'installment' ? modal.dataset.perMonth : modal.dataset.perShipment;
      }

      const picker = this.#activePicker(card);
      const servings = picker?.querySelector('[data-servings-text]');
      if (servings && option.dataset.servings) servings.textContent = option.dataset.servings;

      const offer = card.querySelector('[data-offer]');
      const template = modal?.dataset.otsPromo || '';
      if (offer) {
        offer.hidden = template === '';
        const priceHtml = `<strong class="banner-takeover__offer-price">${escapeHtml(formatMoney(card.dataset.price))}</strong>`;
        offer.innerHTML = escapeHtml(template).replaceAll('{price}', priceHtml);
      }

      const image = card.querySelector('.banner-takeover__card-image');
      if (image && option.dataset.image) image.src = option.dataset.image;
    }

    #applyOtsPrices() {
      const cards = this.primary?.track?.querySelectorAll('[data-product-id]:not([data-clone])') || [];
      cards.forEach((card) => {
        if (card.querySelector('[data-pay-select] option')) this.#applySelectedOption(card);
      });
    }

    #applyPayMode() {
      if (this.purchaseType !== 'subscription' || !this.primary?.track) return;

      for (const card of this.#originalCards()) {
        card.querySelectorAll('[data-pay-picker]').forEach((picker) => {
          picker.hidden = picker.dataset.payPicker !== this.payMode;
        });

        const picker = card.querySelector(`[data-pay-picker="${this.payMode}"]`);
        const hasOption = Boolean(picker?.querySelector('option'));
        card.hidden = !hasOption;
        if (hasOption) this.#applySelectedOption(card);
      }
    }

    #onVariantChange(select) {
      const card = select.closest('[data-product-id]');
      const track = card?.closest('[data-track]');
      if (!card || !track) return;

      const productId = card.dataset.productId;
      track.querySelectorAll(`[data-product-id="${productId}"]`).forEach((copy) => {
        const copySelect = copy.querySelector(`[data-pay-select="${select.dataset.paySelect}"]`);
        if (copySelect && copySelect.value !== select.value) copySelect.value = select.value;
        this.#applySelectedOption(copy);
      });

      if (this.purchaseType === 'subscription') {
        this.#applyGiftVisibility();
        this.secondary?.layout();
      }

      this.#renderSummary();
    }

    #paintShipPlan(planEl, card) {
      const ships = card?.dataset.ships || '';
      if (ships) {
        planEl.hidden = false;
        planEl.textContent = ships;
        return;
      }

      const variantId = card?.dataset.variantId || '';
      const request = this.#shipRequest + 1;
      this.#shipRequest = request;
      planEl.hidden = true;
      planEl.textContent = '';
      if (!variantId) return;

      deliveryInterval(variantId).then((policy) => {
        if (request !== this.#shipRequest || this.primary?.current !== card) return;
        if (!policy) return;

        const label = formatShips(this.activeModal?.dataset.shipsTemplate, policy.interval, policy.intervalCount);
        if (!label) return;

        planEl.hidden = false;
        planEl.textContent = label;
      });
    }

    #setSummaryImage(image, card) {
      if (!image) return;

      const src = cardImage(card);
      image.hidden = !src;
      if (src) image.src = src;
    }

    #syncGlow() {
      const wrap = this.activeModal?.querySelector('.banner-takeover__stage-wrap');
      if (!wrap) return;

      const colors = ['#f35e68', '#cc3874', '#9d5199', '#55799d', '#44a779'];
      const primaryIndex = Number(this.primary?.current?.dataset.loopIndex ?? this.primary?.index) || 0;
      const secondaryIndex = Number(this.secondary?.current?.dataset.loopIndex ?? this.secondary?.index) || 0;
      const primaryColor = primaryIndex % colors.length;
      let secondaryColor = secondaryIndex % colors.length;
      if (secondaryColor === primaryColor) secondaryColor = (secondaryColor + 2) % colors.length;

      wrap.style.setProperty('--banner-takeover-glow-primary', colors[primaryColor]);
      wrap.style.setProperty('--banner-takeover-glow-secondary', colors[secondaryColor]);
    }

    #renderSummary() {
      this.#syncGlow();
      const modal = this.activeModal;
      if (!modal || !this.primary || !this.secondary || this.#lock) return;

      const paid = this.primary.current;
      const gift = this.secondary.current;
      const installment = this.purchaseType === 'subscription' && this.payMode === 'installment';
      const paidPrice = Number(paid?.dataset.price) || 0;
      const giftPrice = Number(gift?.dataset.price) || 0;
      const paidCompare = Number(paid?.dataset.compare) || paidPrice;
      const giftCompare = Number(gift?.dataset.compare) || giftPrice;
      const names = [paid?.dataset.title, gift?.dataset.title].filter(Boolean).join(' + ');
      const namesEl = modal.querySelector('[data-summary-names]');
      const priceEl = modal.querySelector('[data-summary-price]');
      const compareEl = modal.querySelector('[data-summary-compare]');
      const saveEl = modal.querySelector('[data-summary-save]');
      const planEl = modal.querySelector('[data-summary-plan]');
      const noteEl = modal.querySelector('[data-summary-note]');
      const addButton = modal.querySelector('[data-add]');
      const addLabel = modal.querySelector('[data-add-label]');

      this.#setSummaryImage(modal.querySelector('[data-summary-image]'), paid);
      this.#setSummaryImage(modal.querySelector('[data-summary-image-secondary]'), gift);
      if (namesEl) namesEl.textContent = names;

      if (this.purchaseType === 'subscription') {
        const worth = Number(gift?.dataset.worth) || 0;
        if (priceEl) {
          const suffix = installment ? modal.dataset.perMonth : modal.dataset.perShipment;
          const unit = suffix ? `<span class="banner-takeover__summary-unit">${escapeHtml(suffix)}</span>` : '';
          priceEl.innerHTML = `${escapeHtml(formatMoney(paidPrice))}${unit}`;
        }
        if (compareEl) compareEl.hidden = true;
        if (saveEl) {
          saveEl.hidden = worth <= 0;
          saveEl.textContent = worth ? modal.dataset.worthTemplate.replace('@@', formatMoney(worth)) : '';
        }
        if (noteEl) noteEl.textContent = modal.dataset.subscriptionNote || '';
        if (planEl) this.#paintShipPlan(planEl, paid);
        if (addLabel && addButton?.dataset.loading !== 'true') addLabel.textContent = `${modal.dataset.addLabel} · ${formatMoney(paidPrice)}`;
        if (addButton && addButton.dataset.loading !== 'true') addButton.disabled = !paid || paid.dataset.available === 'false' || !paid.dataset.planId;
      } else {
        const total = paidPrice + giftPrice;
        const compare = paidCompare + giftCompare;
        const save = compare - total;
        if (priceEl) priceEl.textContent = formatMoney(total);
        if (compareEl) {
          compareEl.hidden = compare <= total;
          compareEl.textContent = formatMoney(compare);
        }
        if (saveEl) {
          saveEl.hidden = save <= 0;
          saveEl.textContent = save > 0 ? modal.dataset.saveTemplate.replace('@@', formatMoney(save)) : '';
        }
        if (planEl) planEl.hidden = true;
        if (noteEl) noteEl.textContent = modal.dataset.oneTimeNote || '';
        if (addLabel && addButton?.dataset.loading !== 'true') addLabel.textContent = `${modal.dataset.addLabel} · ${formatMoney(total)}`;
        if (addButton && addButton.dataset.loading !== 'true') addButton.disabled = !paid || !gift;
      }

      this.#paintPayOptions();
    }

    async #addToCart(dialog) {
      const button = dialog.querySelector('[data-add]');
      if (!button || button.disabled || button.dataset.loading === 'true') return;

      const paid = this.primary?.current;
      const gift = this.secondary?.current;
      if (!paid) return;
      if (this.purchaseType !== 'subscription' && !gift) return;

      const items = [];
      const manual = manualPaymentSelected();
      const payPerMonth = this.purchaseType === 'subscription' && this.payMode === 'installment';

      if (this.purchaseType === 'subscription') {
        const planId = paid.dataset.planId;
        const variantId = paid.dataset.variantId;
        const item = {
          id: Number(variantId),
          quantity: 1,
          properties: lineProperties(variantId, productProperties(paid, {
            subscriptionType: payPerMonth ? 'installment' : 'normal',
            planName: paid.dataset.planName || '',
            otsInstallment: false,
            subscription: true,
            poland: this.dataset.polandFee === 'true'
          }))
        };

        if (planId && manual) item.properties._sellingplan = String(planId);
        if (planId && !manual) item.selling_plan = Number(planId);
        items.push(item);
      } else {
        for (const card of [paid, gift]) {
          items.push({
            id: Number(card.dataset.variantId),
            quantity: 1,
            properties: lineProperties(card.dataset.variantId, productProperties(card, {
              subscriptionType: '',
              planName: '',
              otsInstallment: false,
              subscription: false,
              poland: this.dataset.polandFee === 'true'
            }))
          });
        }
      }

      if (payPerMonth) {
        const fee = await installmentFeeItem(manual);
        if (fee) items.unshift(fee);
      }

      const label = button.querySelector('[data-add-label]');
      const previousLabel = label?.textContent || '';
      button.dataset.loading = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = dialog.dataset.addingLabel || 'Adding...';

      try {
        let addedCart = null;
        if (window.JPCartService?.batchAdd) {
          const added = await window.JPCartService.batchAdd(items);
          addedCart = added?.cart || null;
        } else {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });

          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            const failure = new Error('cart');
            failure.status = response.status;
            failure.body = body;
            throw failure;
          }
        }

        if (payPerMonth && typeof window.ensureInstallmentFeeQuantityOne === 'function') {
          await window.ensureInstallmentFeeQuantityOne(manual);
        }

        await refreshCartChrome(addedCart);
        dialog.close();
        const stayOnPage = this.dataset.mobileAfterAdd === 'stay'
          && window.matchMedia('(max-width: 767px)').matches;
        if (!stayOnPage) openCartDrawer();
      } catch (error) {
        button.disabled = false;
        if (label) label.textContent = previousLabel;
        delete button.dataset.loading;
        button.removeAttribute('aria-busy');

        if (isUnprocessableCart(error)) revealCartFailure(dialog, cartFailureMessage(error));
      }
    }
  }

  customElements.define('banner-takeover', BannerTakeover);
}
